<!--
  - This file is part of the Web3 Library developed by mFactory GmbH.
  -
  - Copyright © 2021, mFactory GmbH
  -
  - Solana Reference Stake Pool is free software: you can redistribute it
  - and/or modify it under the terms of the GNU Affero General Public License
  - as published by the Free Software Foundation, either version 3
  - of the License, or (at your option) any later version.
  -
  - Solana Reference Stake Pool is distributed in the hope that it
  - will be useful, but WITHOUT ANY WARRANTY; without even the implied
  - warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  - See the GNU Affero General Public License for more details.
  -
  - You should have received a copy of the GNU Affero General Public License
  - along with this program.
  - If not, see <https://www.gnu.org/licenses/agpl-3.0.html>.
  -
  - You can be released from the requirements of the Affero GNU General Public License
  - by purchasing a commercial license. The purchase of such a license is
  - mandatory as soon as you develop commercial activities using the
  - Solana Reference Stake Pool code without disclosing the source code of
  - your own applications.
  -
  - The developer of this program can be contacted at <info@mfactory.ch>.
  -->

<script lang="ts">
import { evaPerson } from '@quasar/extras/eva-icons'
import { useWallet } from '@solana/wallet-adapter-vue'
import { defineComponent, ref } from 'vue'
import { useEmitter } from '~/hooks'
import { DELINQ_UPDATE_EVENT } from '~/store'
import { isoTimeDifference, shortenAddress } from '~/utils'

export default defineComponent({
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
    cluster: {
      type: String,
    },
    index: {
      type: Number,
    },
  },
  setup(props) {
    const delinquentTime = ref(
      props.item.isDelinquent ? isoTimeDifference(props.item.lastVote) : '',
    )
    const emitter = useEmitter()
    const { connected } = useWallet()
    if (props.item.isDelinquent) {
      emitter.on(DELINQ_UPDATE_EVENT, () => {
        delinquentTime.value = isoTimeDifference(props.item.lastVote)
      })
    }
    return {
      connected,
      evaPerson,
      name: props.item.name ?? props.item.id,
      buttonProps({ href }) {
        return {
          to: href,
        }
      },
      shortAddress: props.item?.voter ? shortenAddress(props.item.voter, 7) : '',
      delinquentTime,
    }
  },
})
</script>

<template>
  <router-link :to="`/app/${item.voter}`">
    <div class="validator-row-mob row justify-between items-center q-pb-xs relative-position">
      <div class="validator-row-mob__index column q-mr-xs justify-center items-center">
        {{ index }}
      </div>
      <div
        v-if="!loading"
        class="validator-row-mob__logo column q-mr-sm justify-center relative-position"
      >
        <router-link :to="`/app/${item.voter}`">
          <q-avatar class="shadow-1" size="34px">
            <q-img :key="item.voter" :src="item.image" spinner-size="20px" spinner-color="white">
              <template v-if="!item.image" #default>
                <q-icon :name="evaPerson" />
              </template>
              <template #error>
                <q-icon :name="evaPerson" />
              </template>
            </q-img>
          </q-avatar>
        </router-link>
      </div>
      <div v-if="!loading" class="validator-row-mob__name column justify-start">
        <div>
          {{ name }}
          <q-tooltip class="text-body2" :class="{ 'break-words': !item.name }">
            {{ name }}
          </q-tooltip>
        </div>
      </div>
      <div
        v-if="!loading"
        class="validator-row-mob column justify-center no-wrap relative-position"
      >
        <div class="row justify-between relative-position">
          <div class="validator-row-mob__apy column q-pl-sm justify-start">
            <div class="validator-row-mob__apy__val q-mr-auto">
              <b>{{ item.apyEst }}</b>
              <q-tooltip class="text-body2">
                Estimated APY for current epoch
              </q-tooltip>
            </div>
          </div>
          <div class="validator-row-mob__stake no-wrap column q-pl-sm q-mr-sm justify-start">
            <div class="row justify-between">
              <div class="validator-row-mob__stake column justify-start">
                <div class="column validator-row-mob__stake__values">
                  <div class="validator-row-mob__stake__sol">
                    <b class="sol-xs">{{ item.totalSolStacked }}&nbsp;SOL</b>
                    <b class="sol-xxs">{{ item.totalSolStacked.split('.')[0] }}</b>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <validator-row-mob-skeleton v-else />
    </div>
  </router-link>
</template>
